/**
 *  @class
 *  @function FreeShippingProgressBar
 */

if (!customElements.get('free-shipping-progress-bar')) {
    let currentPercent = 0; // Store the current percentage globally
    const drawerStates = new Map();
    let upgrading = true;
    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData }) => {
        if (cartData && cartData.item_count === 0) {
          drawerStates.forEach(state => { state.percent = 0; state.eligible = false; });
        }
      });
    }
    class FreeShippingProgressBar extends HTMLElement {
      constructor() {
        super();
      }
  
      connectedCallback() {
        if (this.classList.contains('zenify-lotus-cart')) {
          this.lotus = this.querySelector('.zenify-cart-lotus');
          this.drawer = this.closest('cart-drawer');
          this.drawerWasActive = this.drawer.classList.contains('active');
          this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
          this.drawerKey = `${this.dataset.country}:${Shopify.currency.active}`;
          this.initialUpgrade = upgrading;
          this.lotus.addEventListener('animationend', event => {
            if (event.animationName === 'zenify-lotus-arrive') this.lotus.classList.remove('zenify-lotus-arrival');
          });
          this.visibilityObserver = new MutationObserver(() => {
            const active = this.drawer.classList.contains('active');
            if (active) this.tryLotusArrival();
            else if (this.drawerWasActive) { this.pendingArrival = false; this.lotus.classList.remove('zenify-lotus-arrival'); }
            this.drawerWasActive = active;
          });
          this.visibilityObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
        }
        this.updateStatus();
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.lotusFrame);
        this.visibilityObserver?.disconnect();
      }
  
      updateStatus() {
        const remainingAmountText = this.querySelector('.free-shipping__info-remaining strong');
        const cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
        const freeShippingThreshold = parseInt(this.dataset.shippingAmount, 10) || 0;
        if (freeShippingThreshold <= 0) {
          this.hidden = true;
          return;
        }
        const remainingInfo = this.querySelector('.free-shipping__info-remaining');
        const eligibilityInfo = this.querySelector('.free-shipping__info-eligibility');
        const isEligible = cartTotal >= freeShippingThreshold;
  
        if (remainingAmountText && !isEligible) {
          const remainingAmount = freeShippingThreshold - cartTotal;
          const currencyFormat = window.theme.settings.money_with_currency_format || "${{amount}}";
          remainingAmountText.innerHTML = formatMoney(remainingAmount, currencyFormat);
        }
  
        remainingInfo.style.display = isEligible ? 'none' : 'block';
        eligibilityInfo.style.display = isEligible ? 'block' : 'none';
  
        const progressPercentage = isEligible ? 1 : cartTotal / freeShippingThreshold;
        if (this.lotus) this.animateLotusProgress(progressPercentage, isEligible);
        else this.animateProgressBar(progressPercentage);
      }

      animateLotusProgress(progressPercentage, isEligible) {
        cancelAnimationFrame(this.lotusFrame);
        const previous = drawerStates.get(this.drawerKey) || { percent: 0, eligible: this.initialUpgrade && isEligible };
        const startPercent = previous.percent;
        const targetPercent = Math.max(0, Math.min(100, progressPercentage * 100));
        this.pendingArrival = isEligible && !previous.eligible;
        previous.eligible = isEligible;
        drawerStates.set(this.drawerKey, previous);
        this.classList.toggle('zenify-lotus-complete', isEligible);
        this.progressDone = false;
        const duration = this.reducedMotion.matches ? 0 : 440;
        const startTime = performance.now();
        const animate = time => {
          if (!this.isConnected) return;
          const progress = duration ? Math.min((time - startTime) / duration, 1) : 1;
          previous.percent = startPercent + (targetPercent - startPercent) * progress * (2 - progress);
          this.style.setProperty('--percent', `${previous.percent}%`);
          if (progress < 1) this.lotusFrame = requestAnimationFrame(animate);
          else { this.progressDone = true; this.tryLotusArrival(); }
        };
        animate(startTime);
      }

      tryLotusArrival() {
        if (!this.pendingArrival || !this.progressDone || !this.drawer.classList.contains('active')) return;
        this.pendingArrival = false;
        if (!this.reducedMotion.matches && !document.hidden) this.lotus.classList.add('zenify-lotus-arrival');
      }
  
      animateProgressBar(progressPercentage) {
        const targetPercent = progressPercentage * 100;
        const startPercent = currentPercent;
        const duration = 200;
        const startTime = performance.now();
  
        const ease = (t) => t * (2 - t); 
  
        const animate = (time) => {
          const elapsed = time - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easedProgress = ease(progress);
  
          currentPercent = startPercent + (targetPercent - startPercent) * easedProgress;
          this.style.setProperty('--percent', `${currentPercent}%`);
  
          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            currentPercent = targetPercent; 
            this.style.setProperty('--percent', `${currentPercent}%`);
          }
        };
  
        requestAnimationFrame(animate);
      }
    }
  
    customElements.define('free-shipping-progress-bar', FreeShippingProgressBar);
    upgrading = false;
}
